import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getVideoPodcastById,
  toggleLikeVideoPodcast,
  convertVideoToMP4,
  deleteVideoPodcast,
  getVideoPodcastComments,
  addVideoPodcastComment,
  deleteVideoPodcastComment,
  getAllVideoPodcasts,
  getPublishedPodcastsByAuthor,
} from '../../api/videoPodcast';
import { submitFeedback, captureDeviceInfo } from '../../api/feedback';
import { useAuth } from '../../components/AuthContext';
import OptimizedVideoPlayer from '../../components/OptimizedVideoPlayer';
import ShareMenu from '../../components/ShareMenu';
import FollowUnfollowButton from '../../components/FollowUnfollowButton';
import VideoPodcastCard from '../../components/VideoPodcastCard';
import {
  formatPodcastTime,
  getPodcastAuthorName,
  getPodcastShareUrl,
  getPodcastLikesCount,
  getPodcastVideoSource,
  resolvePodcastAuthorPhoto,
  resolvePodcastThumbnail,
} from '../../utils/podcastUtils';
import { formatDistanceToNow } from 'date-fns';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import toast from 'react-hot-toast';

const SAVED_PODCASTS_KEY = 'saved_podcasts';
const SHARE_COUNTS_KEY = 'podcast_share_counts';

const LANGUAGE_LABELS = {
  en: 'English',
  hi: 'Hindi',
  kn: 'Kannada',
  te: 'Telugu',
  ta: 'Tamil',
  ml: 'Malayalam',
  mr: 'Marathi',
  bn: 'Bengali',
  gu: 'Gujarati',
  pa: 'Punjabi',
};

const readJsonObject = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const readSavedPodcasts = () => {
  try {
    const raw = localStorage.getItem(SAVED_PODCASTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  };
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const ActionButton = ({ icon, label, active = false, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition ${
      active
        ? 'border-app-accent bg-app-accent/10 text-app-accent'
        : 'border-app-border bg-app-card text-app-text hover:bg-app-hover'
    } ${className}`}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const StatPill = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-app-hover px-3 py-1 text-xs font-medium text-app-muted">
    {icon}
    {children}
  </span>
);

const VideoPodcastDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  const commentsRef = useRef(null);

  const [podcast, setPodcast] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedPodcasts, setRelatedPodcasts] = useState([]);
  const [creatorPodcasts, setCreatorPodcasts] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  const authorId = useMemo(
    () => String(podcast?.authorId?._id || podcast?.authorId || ''),
    [podcast]
  );

  const isOwner = useMemo(() => {
    if (!currentUser || !podcast) return false;
    return authorId === String(currentUser.id);
  }, [currentUser, podcast, authorId]);

  const playbackVideo = useMemo(
    () => getPodcastVideoSource(podcast) || podcast?.video || null,
    [podcast]
  );

  const posterUrl = useMemo(() => resolvePodcastThumbnail(podcast), [podcast]);
  const shareUrl = useMemo(() => (id ? getPodcastShareUrl(id) : ''), [id]);

  const tags = useMemo(() => {
    if (!podcast) return [];
    const list = [];
    if (Array.isArray(podcast.tags)) {
      list.push(...podcast.tags.map((tag) => String(tag).trim()).filter(Boolean));
    }
    if (podcast.category) list.push(podcast.category);
    const hashtags = (podcast.description || '').match(/#[\w-]+/g);
    if (hashtags) {
      list.push(...hashtags.map((tag) => tag.replace('#', '')));
    }
    return [...new Set(list.filter(Boolean))];
  }, [podcast]);

  const podcastDescription = useMemo(() => {
    const text = String(podcast?.description || '').trim();
    if (!text) return '';
    const normalized = text.replace(/\.+$/, '').toLowerCase();
    if (normalized === 'no description available' || normalized === 'no description') return '';
    return text;
  }, [podcast?.description]);

  const languageLabel = useMemo(() => {
    const code = String(podcast?.language || 'en').toLowerCase();
    return LANGUAGE_LABELS[code] || code.toUpperCase();
  }, [podcast?.language]);

  const fetchComments = async () => {
    if (!id) return;
    try {
      setCommentsLoading(true);
      const data = await getVideoPodcastComments(id);
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const fetchRelatedContent = useCallback(async (currentPodcast) => {
    if (!currentPodcast?._id) return;
    setRelatedLoading(true);
    try {
      const category = currentPodcast.category || null;
      const [relatedResult, creatorResult] = await Promise.all([
        getAllVideoPodcasts(1, 12, category, null, null).catch(() => ({ podcasts: [] })),
        authorId
          ? getPublishedPodcastsByAuthor(authorId, 1, 12).catch(() => ({ podcasts: [] }))
          : Promise.resolve({ podcasts: [] }),
      ]);

      const currentId = String(currentPodcast._id);
      const related = (relatedResult?.podcasts || [])
        .filter((item) => String(item._id) !== currentId)
        .slice(0, 6);
      const fromCreator = (creatorResult?.podcasts || [])
        .filter((item) => String(item._id) !== currentId)
        .slice(0, 6);

      setRelatedPodcasts(related);
      setCreatorPodcasts(fromCreator);
    } catch (err) {
      console.error('Error fetching related podcasts:', err);
    } finally {
      setRelatedLoading(false);
    }
  }, [authorId]);

  const fetchPodcast = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVideoPodcastById(id);
      setPodcast(data);

      if (currentUser && data.likes) {
        const userLiked = data.likes.some((like) =>
          (typeof like === 'object' ? like._id : like).toString() === currentUser.id.toString()
        );
        setIsLiked(userLiked);
      }

      await fetchRelatedContent(data);
    } catch (err) {
      console.error('Error fetching video podcast:', err);
      setError(err.message || 'Failed to load video podcast');
      toast.error(err.message || 'Failed to load video podcast');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPodcast();
      fetchComments();
      const counts = readJsonObject(SHARE_COUNTS_KEY);
      setShareCount(Number(counts[id] || 0));
      setIsSaved(readSavedPodcasts().some((item) => String(item._id) === String(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleShare = () => {
    const counts = readJsonObject(SHARE_COUNTS_KEY);
    const next = Number(counts[id] || 0) + 1;
    counts[id] = next;
    localStorage.setItem(SHARE_COUNTS_KEY, JSON.stringify(counts));
    setShareCount(next);
  };

  const handleSave = () => {
    if (!podcast) return;
    const existing = readSavedPodcasts();
    const alreadySaved = existing.some((item) => String(item._id) === String(podcast._id));

    if (alreadySaved) {
      const next = existing.filter((item) => String(item._id) !== String(podcast._id));
      localStorage.setItem(SAVED_PODCASTS_KEY, JSON.stringify(next));
      setIsSaved(false);
      toast.success('Removed from saved');
      return;
    }

    const savedItem = {
      _id: podcast._id,
      title: podcast.title,
      description: podcast.description || '',
      author: getPodcastAuthorName(podcast),
      authorId,
      category: podcast.category || 'General',
      thumbnail: resolvePodcastThumbnail(podcast),
      savedAt: new Date().toISOString(),
      articleType: 'podcast',
    };
    localStorage.setItem(SAVED_PODCASTS_KEY, JSON.stringify([savedItem, ...existing]));
    setIsSaved(true);
    toast.success('Podcast saved');
  };

  const handleLike = async () => {
    if (!currentUser || !token) {
      toast.error('Please login to like podcasts');
      navigate('/login');
      return;
    }

    try {
      const result = await toggleLikeVideoPodcast(id, currentUser.id, token);
      setIsLiked(result.isLiked);
      setPodcast((prev) => ({
        ...prev,
        likes: result.isLiked
          ? [...(prev.likes || []), currentUser.id]
          : (prev.likes || []).filter(
              (likeId) =>
                (typeof likeId === 'object' ? likeId._id : likeId).toString() !== currentUser.id.toString()
            ),
      }));
      toast.success(result.isLiked ? 'Liked!' : 'Unliked');
    } catch (err) {
      toast.error(err.message || 'Failed to toggle like');
    }
  };

  const handleDelete = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!window.confirm('Delete this podcast permanently?')) return;
    try {
      await deleteVideoPodcast(id, token);
      toast.success('Podcast deleted');
      navigate('/video-podcasts');
    } catch (err) {
      toast.error(err.message || 'Failed to delete podcast');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!currentUser || !token) {
      toast.error('Please login to comment');
      navigate('/login');
      return;
    }
    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      const comment = await addVideoPodcastComment(id, commentText.trim(), currentUser.id, token);
      setComments((prev) => [comment, ...prev]);
      setCommentText('');
      toast.success('Comment added');
    } catch (err) {
      toast.error(err.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!token) return;
    try {
      await deleteVideoPodcastComment(id, commentId, token);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete comment');
    }
  };

  const handleConvertToMP4 = async () => {
    if (!currentUser || !token) {
      toast.error('Please login to convert videos');
      navigate('/login');
      return;
    }

    setIsConverting(true);
    try {
      toast.loading('Converting video to MP4 format...', { id: 'convert' });
      const result = await convertVideoToMP4(id, token);
      if (result.success) {
        toast.success('Video converted successfully! Refreshing...', { id: 'convert' });
        await fetchPodcast();
      } else {
        throw new Error(result.message || 'Conversion failed');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to convert video', { id: 'convert' });
    } finally {
      setIsConverting(false);
    }
  };

  const scrollToComments = () => {
    commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportText.trim() || reportText.trim().length < 10) {
      toast.error('Please provide at least 10 characters');
      return;
    }

    setSubmittingReport(true);
    try {
      await submitFeedback(
        {
          message: `Podcast report (${shareUrl}): ${reportText.trim()}`,
          type: 'Feedback',
          deviceInfo: captureDeviceInfo(),
        },
        token || null
      );
      toast.success('Report submitted. Thank you.');
      setReportOpen(false);
      setReportText('');
    } catch (err) {
      toast.error(err.message || 'Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const renderLikeButton = (size = 'default') => {
    const isLarge = size === 'large';
    return (
      <button
        type="button"
        onClick={handleLike}
        className={`inline-flex items-center gap-2 font-semibold transition ${
          isLarge ? 'rounded-xl px-5 py-2.5 text-sm' : 'rounded-full border px-3 py-2 text-sm'
        } ${
          isLiked
            ? isLarge
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'border-blue-600 bg-blue-50 text-blue-700'
            : isLarge
              ? 'border border-app-border bg-app-card text-app-text hover:bg-app-hover'
              : 'border-app-border bg-app-card text-app-text hover:border-app-border hover:bg-app-hover'
        }`}
      >
        {isLiked ? <ThumbUpIcon sx={{ fontSize: isLarge ? 20 : 18 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: isLarge ? 20 : 18 }} />}
        {isLiked ? 'Liked' : 'Like'}
        <span className={`${isLiked && isLarge ? 'text-blue-100' : 'text-app-muted'} text-xs font-medium`}>
          {getPodcastLikesCount(podcast)}
        </span>
      </button>
    );
  };

  const renderActionButtons = () => (
    <div className="flex flex-wrap items-center gap-2">
      {renderLikeButton()}
      <ActionButton
        icon={<CommentIcon sx={{ fontSize: 18 }} />}
        label="Comment"
        onClick={scrollToComments}
      />
      <ShareMenu
        articleUrl={shareUrl}
        articleTitle={podcast.title}
        onShared={handleShare}
      />
    </div>
  );

  const openAuthorProfile = () => {
    if (authorId) navigate(`/profile/${authorId}`);
  };

  if (loading) {
    return (
      <div className="app-page flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className="app-page mx-auto max-w-5xl p-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 rounded-full border border-app-border bg-app-card px-3 py-1.5 text-sm font-medium text-app-text hover:bg-app-hover"
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
          Back
        </button>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error || 'Video podcast not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="app-page min-h-screen pb-12">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-1 rounded-full border border-app-border bg-app-card px-3 py-1.5 text-sm font-medium text-app-text transition hover:bg-app-hover"
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
          Back
        </button>

        <div className="overflow-hidden rounded-2xl border border-app-border bg-black shadow-lg">
          <OptimizedVideoPlayer
            video={playbackVideo}
            className="w-full"
            showControls
            aspectRatio="aspect-video"
            poster={posterUrl}
          />
        </div>

        {podcast.video && podcast.video.toLowerCase().endsWith('.webm') && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <p className="text-sm text-amber-900">
                WebM format detected. Convert to MP4 for better browser compatibility.
              </p>
              {currentUser && token && (
                <button
                  type="button"
                  onClick={handleConvertToMP4}
                  disabled={isConverting}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  <RefreshIcon className={isConverting ? 'animate-spin' : ''} sx={{ fontSize: 18 }} />
                  {isConverting ? 'Converting...' : 'Convert to MP4'}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="app-card mt-6 rounded-2xl border p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h1 className="text-2xl font-bold leading-tight text-app-text sm:text-3xl">
              {podcast.title}
            </h1>
            <p className="mt-2 text-sm text-app-muted">
              Uploaded {formatPodcastTime(podcast.publishedAt || podcast.createdAt)}
            </p>
          </div>

          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {podcast.category && (
                <StatPill icon={<CategoryIcon sx={{ fontSize: 14 }} />}>{podcast.category}</StatPill>
              )}
              <StatPill icon={<LanguageIcon sx={{ fontSize: 14 }} />}>{languageLabel}</StatPill>
              {podcast.duration ? (
                <StatPill icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}>{formatDuration(podcast.duration)}</StatPill>
              ) : null}
              {podcast.publishedAt && (
                <StatPill icon={<ScheduleIcon sx={{ fontSize: 14 }} />}>
                  Published {formatDistanceToNow(new Date(podcast.publishedAt), { addSuffix: true })}
                </StatPill>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {isOwner ? (
                <>
                  {renderLikeButton('large')}
                  <button
                    type="button"
                    onClick={() => navigate(`/video-podcasts/${id}/edit`)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-app-hover px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-elevated"
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                    Delete
                  </button>
                </>
              ) : (
                renderActionButtons()
              )}
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: <VisibilityIcon sx={{ fontSize: 18 }} />, label: 'Views', value: podcast.views || 0 },
              { icon: <ThumbUpIcon sx={{ fontSize: 18 }} />, label: 'Likes', value: getPodcastLikesCount(podcast) },
              { icon: <CommentIcon sx={{ fontSize: 18 }} />, label: 'Comments', value: comments.length },
              { icon: <IosShareOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Shares', value: shareCount },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-app-border bg-app-hover px-3 py-3 text-center"
              >
                <div className="mb-1 flex justify-center text-app-muted">{stat.icon}</div>
                <p className="text-lg font-bold text-app-text">{stat.value}</p>
                <p className="text-xs text-app-muted">{stat.label}</p>
              </div>
            ))}
          </div>

          {podcastDescription ? (
            <p className="mb-6 whitespace-pre-wrap text-base leading-relaxed text-app-secondary">
              {podcastDescription}
            </p>
          ) : null}

          {tags.length > 0 && (
            <div className="mb-6">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-app-text">
                <LocalOfferOutlinedIcon sx={{ fontSize: 16 }} />
                Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-app-accent/10 px-3 py-1 text-xs font-medium text-app-accent"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8 flex items-center gap-4 rounded-xl border border-app-border bg-app-hover p-4">
            <button type="button" onClick={openAuthorProfile} className="shrink-0">
              {resolvePodcastAuthorPhoto(podcast) ? (
                <img
                  src={resolvePodcastAuthorPhoto(podcast)}
                  alt={getPodcastAuthorName(podcast)}
                  className="h-14 w-14 rounded-full object-cover ring-2 ring-white"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white">
                  <PersonIcon />
                </div>
              )}
            </button>
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={openAuthorProfile}
                className="text-left text-lg font-semibold text-app-text hover:text-app-accent"
              >
                {getPodcastAuthorName(podcast)}
              </button>
              {podcast.authorId?.bio && (
                <p className="mt-0.5 line-clamp-2 text-sm text-app-muted">{podcast.authorId.bio}</p>
              )}
            </div>
            {!isOwner && podcast.authorId && authorId && (
              <FollowUnfollowButton
                targetUser={typeof podcast.authorId === 'object' ? podcast.authorId : { _id: authorId }}
                size="small"
                showConfirmation={false}
                className="!rounded-full !border !border-app-border !bg-app-card !px-3 !py-2 !text-sm !font-medium !text-app-text hover:!bg-app-hover"
              />
            )}
          </div>

          <section ref={commentsRef} className="scroll-mt-24 border-t border-app-border pt-6">
            <h2 className="mb-4 text-xl font-bold text-app-text">
              Comments ({comments.length})
            </h2>
            <form onSubmit={handleAddComment} className="mb-6">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={currentUser ? 'Write a comment...' : 'Login to comment'}
                disabled={!currentUser || submittingComment}
                rows={3}
                className="app-input mb-3 w-full rounded-xl border p-3 outline-none focus:border-app-accent"
              />
              <button
                type="submit"
                disabled={!currentUser || submittingComment || !commentText.trim()}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {submittingComment ? 'Posting...' : 'Post Comment'}
              </button>
            </form>

            {commentsLoading ? (
              <p className="text-sm text-app-muted">Loading comments...</p>
            ) : comments.length === 0 ? (
              <p className="text-sm text-app-muted">No comments yet. Be the first to comment.</p>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => {
                  const commentUserId = String(comment.userId?._id || comment.userId || '');
                  const canDelete =
                    currentUser &&
                    (commentUserId === String(currentUser.id) || isOwner);

                  return (
                    <div key={comment._id} className="rounded-xl border border-app-border bg-app-hover p-4">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="font-semibold text-app-text">{comment.userId?.name || 'User'}</p>
                        <p className="text-xs text-app-muted">
                          {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                      <p className="text-app-secondary">{comment.text}</p>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment._id)}
                          className="mt-2 text-xs font-medium text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {(relatedPodcasts.length > 0 || relatedLoading) && (
          <section className="mt-8">
            <h2 className="mb-4 text-xl font-bold text-app-text">Related Podcasts</h2>
            {relatedLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {relatedPodcasts.map((item) => (
                  <VideoPodcastCard key={item._id} podcast={item} />
                ))}
              </div>
            )}
          </section>
        )}

        {(creatorPodcasts.length > 0 || relatedLoading) && (
          <section className="mt-10">
            <h2 className="mb-4 text-xl font-bold text-app-text">More from this Creator</h2>
            {relatedLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {creatorPodcasts.map((item) => (
                  <VideoPodcastCard key={item._id} podcast={item} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="app-modal w-full max-w-md rounded-2xl border p-5 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-app-text">Report Podcast</h3>
            <p className="mb-4 text-sm text-app-muted">
              Tell us why this podcast should be reviewed.
            </p>
            <form onSubmit={handleReportSubmit}>
              <textarea
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                rows={4}
                placeholder="Describe the issue..."
                className="app-input mb-4 w-full rounded-xl border p-3 outline-none focus:border-app-accent"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setReportOpen(false);
                    setReportText('');
                  }}
                  className="rounded-xl px-4 py-2 text-sm font-medium text-app-muted hover:bg-app-hover"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReport}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {submittingReport ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPodcastDetailPage;
